import React from 'react';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import TransitionLink from '../layout/TransitionLink';
import SEO from '../seo/SEO';
import ArticleSchema from '../seo/ArticleSchema';
import Footer from '../layout/Footer';
import { formatDate, getReadingTimeText } from '../../data/blog';

export interface ArticleStat {
  value: string;
  label: string;
}

export interface ArticleSection {
  title: string;
  paragraphs: string[];
  code?: { filename: string; content: string };
  stats?: ArticleStat[];
}

export interface ArticleTakeaway {
  title: string;
  description: string;
}

interface OpArticleProps {
  darkMode: boolean;
  slug: string;
  category: string;
  title: string;
  description: string;
  date: string;
  readingTime: number;
  tags: string[];
  image?: string;
  seoKeywords?: string;
  sections: ArticleSection[];
  takeaways: ArticleTakeaway[];
}

/**
 * Shared blog article layout in the Optimus design system. Each post page
 * supplies translated strings; this component is purely presentational.
 * Renders its own Footer because App.tsx excludes /blog/* from the global one.
 */
const OpArticle: React.FC<OpArticleProps> = ({
  darkMode,
  slug,
  category,
  title,
  description,
  date,
  readingTime,
  tags,
  image,
  seoKeywords,
  sections,
  takeaways,
}) => {
  const { t, i18n } = useTranslation();
  const url = `https://talkhaled.com/blog/${slug}`;

  // Serif title with a muted tail, matching the project pages
  const titleWords = title.split(' ');
  const splitAt = Math.min(3, Math.max(1, titleWords.length - 1));
  const titleHead = titleWords.slice(0, splitAt).join(' ');
  const titleTail = titleWords.slice(splitAt).join(' ');

  return (
    <main className="op-page noise-overlay relative min-h-screen overflow-x-hidden">
      <SEO
        pageType="blog"
        title={`${title} | Talal Alkhaled`}
        description={description.slice(0, 155)}
        keywords={seoKeywords}
        image={image}
        url={url}
        type="article"
      />
      <ArticleSchema
        title={title}
        description={description}
        datePublished={date}
        url={url}
        image={image || '/optimized/Talal-md.webp'}
        category={category}
        tags={tags}
        readingTime={readingTime}
        wordCount={sections.reduce((n, s) => n + s.paragraphs.join(' ').split(' ').length, 0)}
      />

      <article className="max-w-[860px] mx-auto px-6 lg:px-8 pt-32 lg:pt-40 pb-24 lg:pb-32">
        {/* Back link */}
        <TransitionLink
          to="/blog"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-12 group"
        >
          <ArrowLeft className="w-4 h-4 rtl:rotate-180 transition-transform group-hover:-translate-x-1 rtl:group-hover:translate-x-1" />
          {t('op.blogPage.back')}
        </TransitionLink>

        {/* Header */}
        <header className="mb-14">
          <span className="inline-flex items-center gap-3 text-sm font-mono text-muted-foreground mb-6">
            <span className="w-8 h-px bg-foreground/30" />
            {category}
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-[3.4rem] font-display tracking-tight leading-[1.05] mb-6">
            {titleHead}
            {titleTail && (
              <>
                {' '}
                <span className="text-muted-foreground">{titleTail}</span>
              </>
            )}
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed mb-8">
            {description}
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-xs text-muted-foreground pb-8 border-b border-foreground/10">
            <span dir="ltr">{formatDate(date, i18n.language)}</span>
            <span className="text-foreground/20">|</span>
            <span>{getReadingTimeText(readingTime, i18n.language)}</span>
            <span className="text-foreground/20">|</span>
            <span>Talal Alkhaled</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-6">
            {tags.slice(0, 6).map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 border border-foreground/15 text-xs font-mono text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        </header>

        {/* Sections */}
        <div className="space-y-16">
          {sections.map((section, i) => (
            <section key={i}>
              <div className="flex items-baseline gap-5 mb-5">
                <span className="shrink-0 font-mono text-sm text-muted-foreground">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h2 className="text-2xl lg:text-3xl font-display tracking-tight">
                  {section.title}
                </h2>
              </div>
              <div className="space-y-5">
                {section.paragraphs.map((p, j) => (
                  <p key={j} className="text-muted-foreground leading-relaxed text-[1.05rem]">
                    {p}
                  </p>
                ))}
              </div>

              {section.code && (
                <div className="mt-8 border border-foreground/10" dir="ltr">
                  <div className="px-4 py-2 border-b border-foreground/10 font-mono text-xs text-muted-foreground bg-foreground/[0.03]">
                    {section.code.filename}
                  </div>
                  <pre className="p-5 overflow-x-auto text-[13px] leading-relaxed font-mono text-foreground/90 bg-foreground/[0.03]">
                    <code>{section.code.content}</code>
                  </pre>
                </div>
              )}

              {section.stats && (
                <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-foreground/10 border border-foreground/10">
                  {section.stats.map((stat, j) => (
                    <div key={j} className="bg-background p-5 text-center">
                      <div className="text-2xl lg:text-3xl font-display mb-1" dir="ltr">
                        {stat.value.includes('→') ? (
                          // before → after: tint the loss red and the win green
                          <>
                            <span className="text-negative">{stat.value.split('→')[0].trim()}</span>
                            <span className="text-muted-foreground"> → </span>
                            <span className="text-positive">{stat.value.split('→')[1].trim()}</span>
                          </>
                        ) : (
                          <span className="text-positive">{stat.value}</span>
                        )}
                      </div>
                      <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>

        {/* Takeaways */}
        <section className="mt-20">
          <span className="inline-flex items-center gap-3 text-sm font-mono text-muted-foreground mb-6">
            <span className="w-8 h-px bg-foreground/30" />
            {t('op.blogPage.takeaways')}
          </span>
          <div>
            {takeaways.map((item, i) => (
              <div
                key={i}
                className="flex gap-6 lg:gap-10 py-6 border-b border-foreground/10 last:border-b-0 group"
              >
                <span className="shrink-0 font-mono text-sm text-positive pt-1">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="text-xl lg:text-2xl font-display mb-2 group-hover:translate-x-1 transition-transform duration-300">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="mt-16 border border-foreground/10 p-8 lg:p-10">
          <h2 className="text-2xl lg:text-3xl font-display tracking-tight mb-3">
            {t('op.blogPage.ctaTitle')}
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-8 max-w-xl">
            {t('op.blogPage.ctaText')}
          </p>
          <div className="flex flex-wrap gap-4">
            <TransitionLink
              to="/projects"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium bg-foreground text-background hover:bg-foreground/90 transition-colors"
            >
              {t('op.blogPage.ctaProjects')}
              <ArrowUpRight className="w-4 h-4 rtl:-scale-x-100" />
            </TransitionLink>
            <TransitionLink
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium border border-foreground/20 text-foreground hover:bg-foreground/5 transition-colors"
            >
              {t('op.blogPage.ctaContact')}
            </TransitionLink>
          </div>
        </section>
      </article>

      <Footer darkMode={darkMode} />
    </main>
  );
};

export default OpArticle;
